import { fromJS, Map as map } from 'immutable';
import { IntlFormatters } from 'react-intl';

export const SUPPORTED_LANGUAGES = {
  EN: 'en',
  FR: 'fr',
};

export const LANGUAGE_IDS = {
  ENGLISH: 1,
  FRENCH: 2,
};

export const LANGUAGE_TRANSLATION_IDS = map({
  [SUPPORTED_LANGUAGES.EN]: 'languages.english',
  [SUPPORTED_LANGUAGES.FR]: 'languages.french',
});

export const LANGUAGE_DROPDOWN_VALUES = fromJS([
  {
    value: SUPPORTED_LANGUAGES.FR,
    id: SUPPORTED_LANGUAGES.FR,
  },
  {
    value: SUPPORTED_LANGUAGES.EN,
    id: SUPPORTED_LANGUAGES.EN,
  },
]);

export const LANGUAGE_SELECT_VALUES = [
  {
    value: SUPPORTED_LANGUAGES.FR,
    label: 'languages.french',
    key: SUPPORTED_LANGUAGES.FR,
  },
  {
    value: SUPPORTED_LANGUAGES.EN,
    label: 'languages.english',
    key: SUPPORTED_LANGUAGES.EN,
  },
];

// Re-mapping of all Yup error strings to our i18n strings.

const YUP_MIXED = (formatFn: IntlFormatters['formatMessage']) => ({
  default: ({ path }) => formatFn({ id: 'validators.yup.mixed.default' }, { path }),
  required: ({ path }) => formatFn({ id: 'validators.yup.mixed.required' }, { path }),
  defined: ({ path }) => formatFn({ id: 'validators.yup.mixed.defined' }, { path }),
  notNull: ({ path }) => formatFn({ id: 'validators.yup.mixed.notNull' }, { path }),
  oneOf: ({ path, values }) => formatFn({ id: 'validators.yup.mixed.oneOf' }, { path, values }),
  notOneOf: ({ path, values }) => formatFn({ id: 'validators.yup.mixed.notOneOf' }, { path, values }),
  notType: ({ path, type, value, originalValue }) => {
    // eslint-disable-next-line eqeqeq
    const diffValues = originalValue != undefined && originalValue !== value;

    if (type === 'mixed') {
      return diffValues
        ? formatFn({ id: 'validators.yup.mixed.notType.mixedDifferentValues' }, { path, type, value, originalValue })
        : formatFn({ id: 'validators.yup.mixed.notType.mixed' }, { path, type, value });
    } else {
      return diffValues
        ? formatFn({ id: 'validators.yup.mixed.notType.otherDifferentValues' }, { path, value, originalValue })
        : formatFn({ id: 'validators.yup.mixed.notType.other' }, { path, value });
    }
  },
});

const YUP_STRING = (formatFn: IntlFormatters['formatMessage']) => ({
  length: ({ path, length }) => formatFn({ id: 'validators.yup.string.length' }, { path, length }),
  min: ({ path, min }) => formatFn({ id: 'validators.yup.string.min' }, { path, min }),
  max: ({ path, max }) => formatFn({ id: 'validators.yup.string.max' }, { path, max }),
  matches: ({ path, regex }) => formatFn({ id: 'validators.yup.string.matches' }, { path, regex }),
  email: ({ path }) => formatFn({ id: 'validators.yup.string.email' }, { path }),
  url: ({ path }) => formatFn({ id: 'validators.yup.string.url' }, { path }),
  uuid: ({ path }) => formatFn({ id: 'validators.yup.string.uuid' }, { path }),
  trim: ({ path }) => formatFn({ id: 'validators.yup.string.trim' }, { path }),
  lowercase: ({ path }) => formatFn({ id: 'validators.yup.string.lowercase' }, { path }),
  uppercase: ({ path }) => formatFn({ id: 'validators.yup.string.uppercase' }, { path }),
});

const YUP_NUMBER = (formatFn: IntlFormatters['formatMessage']) => ({
  min: ({ path, min }) => formatFn({ id: 'validators.yup.number.min' }, { path, min }),
  max: ({ path, max }) => formatFn({ id: 'validators.yup.number.max' }, { path, max }),
  lessThan: ({ path, less }) => formatFn({ id: 'validators.yup.number.lessThan' }, { path, less }),
  moreThan: ({ path, more }) => formatFn({ id: 'validators.yup.number.moreThan' }, { path, more }),
  positive: ({ path }) => formatFn({ id: 'validators.yup.number.positive' }, { path }),
  negative: ({ path }) => formatFn({ id: 'validators.yup.number.negative' }, { path }),
  integer: ({ path }) => formatFn({ id: 'validators.yup.number.integer' }, { path }),
});

const YUP_DATE = (formatFn: IntlFormatters['formatMessage']) => ({
  min: ({ path, min }) => formatFn({ id: 'validators.yup.date.min' }, { path, min: min.toLocaleString() }),
  max: ({ path, max }) => formatFn({ id: 'validators.yup.date.max' }, { path, max: max.toLocaleString() }),
});

const YUP_BOOLEAN = (formatFn: IntlFormatters['formatMessage']) => ({
  isValue: ({ path, value }) => formatFn({ id: 'validators.yup.boolean.isValue' }, { path, value }),
});

const YUP_OBJECT = (formatFn: IntlFormatters['formatMessage']) => ({
  noUnknown: ({ path, unknown }) => formatFn({ id: 'validators.yup.object.noUnknown' }, { path, unknown }),
});

const YUP_ARRAY = (formatFn: IntlFormatters['formatMessage']) => ({
  min: ({ path, min }) => formatFn({ id: 'validators.yup.array.min' }, { path, min }),
  max: ({ path, max }) => formatFn({ id: 'validators.yup.array.max' }, { path, max }),
  length: ({ path, length }) => formatFn({ id: 'validators.yup.array.min' }, { path, length }),
});

export const YUP_I18N = (formatFn: IntlFormatters['formatMessage']) => ({
  mixed: YUP_MIXED(formatFn),
  string: YUP_STRING(formatFn),
  number: YUP_NUMBER(formatFn),
  date: YUP_DATE(formatFn),
  boolean: YUP_BOOLEAN(formatFn),
  object: YUP_OBJECT(formatFn),
  array: YUP_ARRAY(formatFn),
});
